// src/pages/Cohort/CohortDay10Summary.jsx
// Day-10 Question & Answer Summary — cohort-wise
// Shows per-cohort Yes/No/NA breakdown for each of the 12 Day-10 questions

import React, { useState, useEffect, useCallback } from "react";
import { Spin, Empty, Tooltip, Select, Input, Tag, Button } from "antd";
import {
  ReloadOutlined, SearchOutlined, CheckCircleOutlined,
  CloseCircleOutlined, MinusCircleOutlined, BarChartOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import api from "../../services/api";

// ── Design tokens ────────────────────────────────────────────────
const C = {
  navy:"#1B2D6B", blue:"#1E6FBF", lblue:"#EAF1F8",
  green:"#16a34a", lgreen:"#F0FDF4",
  red:"#dc2626",   lred:"#FEF2F2",
  amber:"#b45309", lamber:"#FFFBEB",
  text1:"#1a2d4a", text2:"#4a6580", text3:"#7a96b0",
  border:"#dce6f0", surf:"#f8fafc", white:"#ffffff",
};

// ── Atoms ─────────────────────────────────────────────────────────
const PctBar = ({ pct = 0, color = C.green, h = 6 }) => (
  <div style={{
    height: h, borderRadius: 99, background: "#e2e8f0",
    overflow: "hidden", minWidth: 40,
  }}>
    <div style={{
      width: `${Math.min(pct, 100)}%`, height: "100%",
      background: color, borderRadius: 99, transition: "width .4s",
    }}/>
  </div>
);

// Traffic-light color based on Yes%
const signalColor = (pct) =>
  pct >= 80 ? C.green : pct >= 50 ? C.amber : C.red;

const signalBg = (pct) =>
  pct >= 80 ? C.lgreen : pct >= 50 ? C.lamber : C.lred;

// q12: "Have you faced any challenges where you need support?" — No = positive (no challenges = good)
// For inverted questions, high No% = positive signal
const INVERTED_KEYS = new Set(["q12"]); // q12: "Have you faced challenges?" — No = positive
// For inverted questions compute "positive pct" = No / (Yes+No)
function positivePct(q) {
  if (!INVERTED_KEYS.has(q.key)) return q.yesPct;
  const responded = q.yes + q.no;
  return responded > 0 ? Math.round((q.no / responded) * 100) : 0;
}
function positiveLabel(q) {
  return INVERTED_KEYS.has(q.key) ? "No" : "Yes";
}

// ── Answer pill ───────────────────────────────────────────────────
const Pill = ({ count, type }) => {
  if (!count) return <span style={{ color: "#e2e8f0", fontSize: 11 }}>—</span>;
  const cfg = {
    yes: { color: C.green,  bg: C.lgreen,  icon: "✓" },
    no:  { color: C.red,    bg: C.lred,    icon: "✗" },
    na:  { color: C.text3,  bg: "#f1f5f9", icon: "–" },
  }[type];
  return (
    <span style={{
      fontWeight: 700, fontSize: 12, color: cfg.color,
      background: cfg.bg, padding: "2px 7px", borderRadius: 20,
      border: `1px solid ${cfg.color}33`,
    }}>
      {cfg.icon} {count}
    </span>
  );
};

// ═════════════════════════════════════════════════════════════════
export default function CohortDay10Summary({ onBack, defaultView = "ctable" }) {
  const [loading,     setLoading]     = useState(false);
  const [data,        setData]        = useState(null);   // { questions, cohorts }
  const [search,      setSearch]      = useState("");
  const [view,        setView]        = useState(defaultView); // "ctable" | "cards" | "qtable"
  const [selected,    setSelected]    = useState("all"); // cohort key or "all"
  const [schedFilter, setSchedFilter] = useState("all"); // "all" | "pratham" | "campus" | other

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/cohort/day10-summary");
      const payload = res.data?.data ?? res.data;
      setData(payload);
    } catch { setData(null); }
    finally   { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div style={{ textAlign: "center", paddingTop: 80 }}>
      <Spin size="large"/>
      <div style={{ marginTop: 14, color: C.text3, fontSize: 13 }}>
        Loading Q&A Call Summary…
      </div>
    </div>
  );

  if (!data) return (
    <Empty description="No Q&A Call Summary data available"
      extra={<Button onClick={load} icon={<ReloadOutlined/>}>Retry</Button>}/>
  );

  const { questions } = data;
  // Only Dec'25+ cohorts — exclude Oct/Nov 2025 batch
  const DEC_2025_MS = dayjs('2025-12-01').valueOf();
  const cohorts = (data.cohorts || []).filter(c =>
    c.cohortDate && dayjs(c.cohortDate).valueOf() >= DEC_2025_MS
  );

  // Unique schedule types in data for tab filter
  const schedTypes = [...new Set(cohorts.map(c => c.scheduleKey || 'pratham'))];

  // Filter cohorts by search + scheduleKey filter
  const filtered = cohorts.filter(c => {
    if (search && !c.cohortName.toLowerCase().includes(search.toLowerCase())) return false;
    if (schedFilter !== 'all' && (c.scheduleKey || 'pratham') !== schedFilter) return false;
    return true;
  });

  // For "all" view — aggregate across all cohorts (per answerType)
  const aggregate = questions.map(q => {
    const aType = q.answerType || 'yesno';

    if (aType === 'rating5' || aType === 'integer') {
      // Average of all numeric responses
      const allNums = cohorts.flatMap(c => {
        const qs = c.questionSummary.find(x => x.key === q.key);
        return (qs?.avg != null && qs?.count > 0)
          ? Array(qs.count).fill(qs.avg)   // approximate: use avg × count
          : [];
      });
      // Better: sum up count*avg per cohort
      let totalSum = 0, totalCount = 0;
      cohorts.forEach(c => {
        const qs = c.questionSummary.find(x => x.key === q.key);
        if (qs?.avg != null && qs?.count > 0) {
          totalSum += qs.avg * qs.count;
          totalCount += qs.count;
        }
      });
      const avg = totalCount > 0 ? Math.round((totalSum / totalCount) * 10) / 10 : null;
      const allMins = cohorts.map(c => c.questionSummary.find(x=>x.key===q.key)?.min).filter(v=>v!=null);
      const allMaxs = cohorts.map(c => c.questionSummary.find(x=>x.key===q.key)?.max).filter(v=>v!=null);
      return { ...q, avg, count: totalCount, responded: totalCount,
        min: allMins.length ? Math.min(...allMins) : null,
        max: allMaxs.length ? Math.max(...allMaxs) : null,
        yes:0, no:0, na:0, unanswered:0, yesPct:0, choiceCounts:{} };
    }

    if (aType === 'choice') {
      const mergedCounts = {};
      let totalResponded = 0;
      cohorts.forEach(c => {
        const qs = c.questionSummary.find(x => x.key === q.key);
        Object.entries(qs?.choiceCounts || {}).forEach(([k,v]) => {
          mergedCounts[k] = (mergedCounts[k]||0) + v;
          totalResponded += v;
        });
      });
      return { ...q, choiceCounts: mergedCounts, responded: totalResponded,
        yes:0, no:0, na:0, unanswered:0, yesPct:0, avg:null, count:totalResponded };
    }

    // yesno
    const totals = cohorts.reduce((acc, c) => {
      const qs = c.questionSummary.find(x => x.key === q.key);
      if (!qs) return acc;
      return {
        yes:        acc.yes        + qs.yes,
        no:         acc.no         + qs.no,
        na:         acc.na         + qs.na,
        responded:  acc.responded  + qs.responded,
        unanswered: acc.unanswered + qs.unanswered,
      };
    }, { yes: 0, no: 0, na: 0, responded: 0, unanswered: 0 });
    const yesPct = totals.responded > 0
      ? Math.round((totals.yes / totals.responded) * 100) : 0;
    return { ...q, ...totals, yesPct, avg:null, choiceCounts:{} };
  });

  // Active cohort for single-cohort view
  const activeCohort = selected === "all"
    ? null
    : filtered.find(c => `${c.cohortName}||${c.cohortDate}` === selected);

  const displayQuestions = selected === "all"
    ? aggregate
    : (activeCohort?.questionSummary || []);

  const totalResponded = selected === "all"
    ? cohorts.reduce((s, c) => s + c.totalCompleted, 0)
    : (activeCohort?.totalCompleted || 0);

  const totalEnrolled = selected === "all"
    ? cohorts.reduce((s, c) => s + c.totalEnrolled, 0)
    : (activeCohort?.totalEnrolled || 0);

  const overallPct = totalEnrolled > 0
    ? Math.round((totalResponded / totalEnrolled) * 100) : 0;

  return (
    <div style={{ marginBottom: 24 }}>

      {/* ── Header ───────────────────────────────────────────── */}
      <div style={{
        background: `linear-gradient(135deg,${C.navy} 0%,#2a4a9f 100%)`,
        borderRadius: 14, padding: "20px 24px", marginBottom: 20,
        display: "flex", justifyContent: "space-between",
        alignItems: "center", flexWrap: "wrap", gap: 12,
      }}>
        <div>
          <div style={{
            fontSize: 18, fontWeight: 800, color: "#fff",
            display: "flex", alignItems: "center", gap: 10,
          }}>
            📋 Q&A Call Summary — Pratham (Day 10) · Campus (Day 30)
          </div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", marginTop: 4 }}>
            {cohorts.length} cohorts · {questions.length} questions ·{" "}
            {totalResponded} Q&A calls completed out of {totalEnrolled} enrolled
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button icon={<ReloadOutlined/>} onClick={load} loading={loading}
            style={{
              background: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.25)",
              color: "#fff", borderRadius: 8,
            }}>
            Refresh
          </Button>
        </div>
      </div>

      {/* ── Summary KPI strip ─────────────────────────────────── */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))",
        gap: 12, marginBottom: 20,
      }}>
        {[
          { label: "Cohorts",        value: cohorts.length,   color: C.navy },
          { label: "Total Enrolled", value: totalEnrolled,    color: C.blue },
          { label: "D10 Completed",  value: totalResponded,   color: C.green,
            note: `${overallPct}% completion` },
          { label: "Questions",      value: questions.length, color: C.amber },
          { label: "Highest Yes%",
            value: displayQuestions.length
              ? `${Math.max(...displayQuestions.filter(q=>(q.answerType||'yesno')==='yesno').map(q => q.yesPct)) || 0}%`
              : "—",
            color: C.green,
            note: displayQuestions.length
              ? displayQuestions.sort((a,b)=>b.yesPct-a.yesPct)[0]?.label?.slice(0,30)+"…"
              : "",
          },
          { label: "Needs Attention",
            value: displayQuestions.filter(q => (q.answerType||'yesno')==='yesno' && q.yesPct < 50 && q.responded > 0).length,
            color: C.red,
            note: "Questions with < 50% Yes",
          },
        ].map((k, i) => (
          <div key={i} style={{
            background: C.white, borderRadius: 12, padding: "14px 16px",
            border: `1.5px solid ${k.color}22`,
            boxShadow: `0 2px 8px ${k.color}10`,
          }}>
            <div style={{
              fontSize: 11, fontWeight: 700, color: C.text3,
              textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: 6,
            }}>{k.label}</div>
            <div style={{
              fontSize: 28, fontWeight: 800, color: k.color, lineHeight: 1,
            }}>{k.value}</div>
            {k.note && (
              <div style={{ fontSize: 10, color: k.color, marginTop: 5,
                fontWeight: 600, opacity: 0.85 }}>
                {k.note}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Cohort Type Tabs (Pratham / Campus) ──────────────── */}
      {schedTypes.length > 1 && (
        <div style={{
          display: "flex", gap: 0, marginBottom: 16,
          background: C.white, borderRadius: 12,
          border: `1px solid ${C.border}`, overflow: "hidden",
        }}>
          {[{key:"all", label:"All Cohorts"}, ...schedTypes.map(k=>({
            key: k,
            label: k === 'pratham' ? '🟦 Pratham (Day 10 Q&A)'
                 : k === 'campus'  ? '🟩 Campus (Day 30 Q&A)'
                 : k.charAt(0).toUpperCase() + k.slice(1),
          }))].map((t, i, arr) => {
            const active = schedFilter === t.key;
            const cohortCount = t.key === 'all' ? cohorts.length
              : cohorts.filter(c => (c.scheduleKey||'pratham') === t.key).length;
            return (
              <button key={t.key} onClick={() => {
                setSchedFilter(t.key);
                setSelected('all');
              }} style={{
                flex: 1, padding: "11px 16px", cursor: "pointer",
                border: "none",
                borderRight: i < arr.length-1 ? `1px solid ${C.border}` : "none",
                borderBottom: active ? `3px solid ${C.navy}` : "none",
                background: active ? C.white : C.surf,
                textAlign: "left",
              }}>
                <div style={{ fontSize: 13, fontWeight: 800,
                  color: active ? C.navy : C.text2 }}>
                  {t.label}
                </div>
                <div style={{ fontSize: 11, color: C.text3, marginTop: 2 }}>
                  {cohortCount} cohort{cohortCount !== 1 ? 's' : ''}
                  {t.key !== 'all' && ` · Q&A on ${t.key === 'pratham' ? 'Day 10' : t.key === 'campus' ? 'Day 30' : '—'}`}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* ── Controls ──────────────────────────────────────────── */}
      <div style={{
        background: C.white, borderRadius: 12, padding: "12px 16px",
        border: `1px solid ${C.border}`, marginBottom: 16,
        display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center",
      }}>
        <Input prefix={<SearchOutlined style={{ color: C.text3 }}/>}
          placeholder="Search cohort…" value={search}
          onChange={e => setSearch(e.target.value)} allowClear
          style={{ width: 200, borderRadius: 8 }}/>

        <Select
          value={selected} onChange={setSelected}
          style={{ width: 260 }}
          options={[
            { value: "all", label: "📊 All Cohorts — Aggregated" },
            ...filtered.map(c => ({
              value: `${c.cohortName}||${c.cohortDate}`,
              label: `${c.cohortName}${c.cohortDate
                ? " · " + dayjs(c.cohortDate).format("DD MMM YY") : ""}${
                c.qaLabel ? " [" + c.qaLabel + "]" : ""}`,
            })),
          ]}
        />


      </div>

      {/* ── Overall completion bar ────────────────────────────── */}
      <div style={{
        background: C.white, borderRadius: 12, padding: "14px 20px",
        border: `1px solid ${C.border}`, marginBottom: 20,
      }}>
        <div style={{
          display: "flex", justifyContent: "space-between",
          alignItems: "center", marginBottom: 8,
        }}>
          <span style={{ fontWeight: 700, fontSize: 13, color: C.text1 }}>
            {selected === "all" ? "Overall Q&A Call Completion" : `${activeCohort?.cohortName} — ${activeCohort?.qaLabel || "Day 10"} Q&A Completion`}
          </span>
          <span style={{
            fontWeight: 800, fontSize: 15,
            color: signalColor(overallPct),
          }}>
            {totalResponded} / {totalEnrolled} completed &nbsp;·&nbsp; {overallPct}%
          </span>
        </div>
        <PctBar pct={overallPct} color={signalColor(overallPct)} h={10}/>
        <div style={{ fontSize: 11, color: C.text3, marginTop: 6 }}>
          {totalEnrolled - totalResponded > 0
            ? `⚠️ ${totalEnrolled - totalResponded} employees yet to have Q&A call logged`
            : "🎉 All enrolled employees have Q&A call completed!"}
        </div>
      </div>

      {/* ══ TAB BAR ════════════════════════════════════════════ */}
      <div style={{
        display: "flex", gap: 0, marginBottom: 0,
        background: C.white, borderRadius: "12px 12px 0 0",
        border: `1px solid ${C.border}`,
        borderBottom: "none", overflow: "hidden",
      }}>
        {[
          { key: "ctable", icon: "📊", label: "Cohort × Question Table", sub: "Cross-tab: cohorts as rows" },
          { key: "cards",  icon: "🃏", label: "Question Cards",          sub: "Per-question Yes/No/Avg breakdown" },
          { key: "qtable", icon: "📋", label: "Question × Cohort Table", sub: "Cross-tab: questions as rows" },
        ].map((t, i, arr) => {
          const active = view === t.key;
          return (
            <button key={t.key} onClick={() => setView(t.key)} style={{
              flex: 1, padding: "13px 16px", cursor: "pointer",
              border: "none", borderRight: i < arr.length-1 ? `1px solid ${C.border}` : "none",
              borderBottom: active ? `2px solid ${C.navy}` : `1px solid ${C.border}`,
              background: active ? C.white : C.surf,
              textAlign: "left", transition: "all .15s",
            }}>
              <div style={{
                fontSize: 13, fontWeight: 800,
                color: active ? C.navy : C.text2,
                display: "flex", alignItems: "center", gap: 6,
              }}>
                {t.icon} {t.label}
              </div>
              <div style={{ fontSize: 11, color: C.text3, marginTop: 2 }}>
                {t.sub}
              </div>
            </button>
          );
        })}
      </div>

      {/* ── Tab panel wrapper ────────────────────────────────── */}
      <div style={{
        border: `1px solid ${C.border}`,
        borderTop: "none",
        borderRadius: "0 0 14px 14px",
        overflow: "hidden",
        marginBottom: 20,
      }}>

      {/* ══ GRID VIEW ═══════════════════════════════════════════ */}
      {view === "cards" && (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill,minmax(340px,1fr))",
          gap: 14, padding: "20px",
          background: "#f8fafc",
        }}>
          {displayQuestions.map((q, idx) => {
            const aTypeCard = q.answerType || 'yesno';
            const posPct = positivePct(q);
            const col = aTypeCard==='rating5'
              ? (q.avg>=4?C.green:q.avg>=3?C.amber:q.avg>0?C.red:C.text3)
              : aTypeCard==='choice' ? C.blue
              : signalColor(posPct);
            const bg  = aTypeCard==='rating5'
              ? (q.avg>=4?C.lgreen:q.avg>=3?C.lamber:q.avg>0?C.lred:C.surf)
              : aTypeCard==='choice' ? C.lblue
              : signalBg(posPct);
            const hasData = q.responded > 0 || (q.count != null && q.count > 0);
            return (
              <div key={q.key} style={{
                background: C.white, borderRadius: 14,
                border: `1.5px solid ${hasData ? col : C.border}22`,
                boxShadow: `0 2px 8px ${hasData ? col : "#94a3b8"}10`,
                overflow: "hidden",
              }}>
                {/* Question header */}
                <div style={{
                  padding: "12px 16px",
                  background: hasData ? bg : C.surf,
                  borderBottom: `1px solid ${C.border}`,
                  display: "flex", alignItems: "flex-start", gap: 10,
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                    background: hasData ? col : C.text3,
                    color: "#fff", display: "flex", alignItems: "center",
                    justifyContent: "center", fontSize: 11, fontWeight: 800,
                  }}>
                    {idx + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: 12, fontWeight: 700, color: C.text1,
                      lineHeight: 1.4,
                    }}>
                      {q.label}
                    </div>
                    <div style={{ fontSize: 10, color: C.text3, marginTop: 2 }}>
                      {q.responded} responses
                      {q.unanswered > 0 && ` · ${q.unanswered} unanswered`}
                    </div>
                  </div>
                  {hasData && (
                    <div style={{ fontSize:18, fontWeight:800, flexShrink:0,
                      color: q.answerType==='rating5'?(q.avg>=4?C.green:q.avg>=3?C.amber:C.red):col }}>
                      {q.answerType==='rating5' ? `${q.avg??'—'}/5`
                        : q.answerType==='choice' ? `${q.responded}`
                        : `${posPct}%`}
                    </div>
                  )}
                </div>

                {/* Answer breakdown */}
                <div style={{ padding: "14px 16px" }}>
                  {!hasData ? (
                    <div style={{ textAlign:"center", color:C.text3, fontSize:12, padding:"12px 0" }}>
                      No responses yet
                    </div>
                  ) : q.answerType === 'rating5' ? (
                    /* ── Rating 1-5 card ── */
                    <>
                      <div style={{ textAlign:"center", marginBottom:12 }}>
                        <span style={{ fontSize:36, fontWeight:900,
                          color: q.avg>=4?C.green:q.avg>=3?C.amber:C.red }}>
                          {q.avg ?? "—"}
                        </span>
                        <span style={{ fontSize:16, color:C.text3, fontWeight:600 }}>/5</span>
                      </div>
                      {/* ★ Star rating */}
                      <div style={{ display:"flex", gap:4, justifyContent:"center", marginBottom:10 }}>
                        {[1,2,3,4,5].map(n => {
                          const filled = q.avg != null && n <= Math.round(q.avg);
                          const halfFilled = q.avg != null && !filled && n - 0.5 <= q.avg;
                          const starCol = filled ? (n<=2?C.red:n===3?C.amber:C.green)
                                       : halfFilled ? (n<=2?C.red:n===3?C.amber:C.green)
                                       : '#e2e8f0';
                          return (
                            <span key={n} style={{
                              fontSize: 28, lineHeight:1,
                              color: starCol,
                              filter: filled||halfFilled ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.15))' : 'none',
                              transition: 'all .3s',
                            }}>
                              {halfFilled && !filled ? '½' : filled ? '★' : '☆'}
                            </span>
                          );
                        })}
                      </div>
                      <div style={{ fontSize:11, color:C.text3, textAlign:"center", marginBottom:10 }}>
                        {q.count} responses · min {q.min} · max {q.max}
                      </div>
                      <div style={{ marginTop:6, padding:"6px 10px", borderRadius:8,
                        background: q.avg>=4?C.lgreen:q.avg>=3?C.lamber:C.lred,
                        border:`1px solid ${q.avg>=4?C.green:q.avg>=3?C.amber:C.red}33`,
                        fontSize:11, fontWeight:700, textAlign:"center",
                        color: q.avg>=4?C.green:q.avg>=3?C.amber:C.red }}>
                        {q.avg>=4 ? "😊 Comfortable" : q.avg>=3 ? "😐 Moderate" : "😟 Needs support"}
                      </div>
                    </>
                  ) : q.answerType === 'choice' ? (
                    /* ── Choice card ── */
                    <>
                      {Object.entries(q.choiceCounts||{}).map(([opt, cnt]) => {
                        const pct = q.responded>0?Math.round((cnt/q.responded)*100):0;
                        const optCol = opt==='Daily'?C.blue:C.purple;
                        return (
                          <div key={opt} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                            <span style={{ width:80, fontSize:11, fontWeight:700, color:optCol, flexShrink:0 }}>
                              {opt==='Daily'?'📅 Daily':'📞 Call/Call'}
                            </span>
                            <div style={{ flex:1 }}>
                              <PctBar pct={pct} color={optCol} h={7}/>
                            </div>
                            <span style={{ fontSize:12, fontWeight:800, color:optCol, minWidth:28, textAlign:"right" }}>{cnt}</span>
                            <span style={{ fontSize:10, color:C.text3, minWidth:32, textAlign:"right" }}>{pct}%</span>
                          </div>
                        );
                      })}
                      <div style={{ marginTop:8, padding:"6px 10px", borderRadius:8,
                        background:C.lblue, border:`1px solid ${C.blue}33`,
                        fontSize:11, fontWeight:700, textAlign:"center", color:C.blue }}>
                        📊 {q.responded} responses recorded
                      </div>
                    </>
                  ) : (
                    /* ── Yes/No/NA card ── */
                    <>
                      {(() => {
                        const isInv = INVERTED_KEYS.has(q.key);
                        return [
                          { label: isInv ? "✓ No (Good)"  : "✓ Yes", count: isInv ? q.no  : q.yes, color: C.green, bg: C.lgreen },
                          { label: isInv ? "✗ Yes (Issue)" : "✗ No",  count: isInv ? q.yes : q.no,  color: C.red,   bg: C.lred  },
                          { label: "– NA", count: q.na, color: C.text3, bg: "#f1f5f9" },
                        ].map(row => {
                          const pct = q.responded>0?Math.round((row.count/q.responded)*100):0;
                          return (
                            <div key={row.label} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                              <span style={{ width:80, fontSize:11, fontWeight:700, color:row.color, flexShrink:0 }}>
                                {row.label}
                              </span>
                              <div style={{ flex:1 }}><PctBar pct={pct} color={row.color} h={7}/></div>
                              <span style={{ fontSize:12, fontWeight:800, color:row.color, minWidth:30, textAlign:"right" }}>{row.count}</span>
                              <span style={{ fontSize:10, color:C.text3, minWidth:32, textAlign:"right" }}>{pct}%</span>
                            </div>
                          );
                        });
                      })()}
                      <div style={{ marginTop:10, padding:"6px 10px", borderRadius:8, background:bg,
                        border:`1px solid ${col}33`, fontSize:11, fontWeight:700, color:col, textAlign:"center" }}>
                        {posPct>=80 ? "😊 Strong positive response"
                          : posPct>=50 ? "😐 Moderate — needs follow-up"
                          : "😟 Low positivity — action required"}
                        {INVERTED_KEYS.has(q.key) && (
                          <span style={{ fontSize:10, fontWeight:400, opacity:.7, marginLeft:6 }}>
                            (No = positive)
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ══ TABLE VIEW — full cross-tab ══════════════════════════ */}
      {view === "qtable" && (
        <div style={{
          background: C.white,
          overflow: "hidden", marginBottom: 16,
        }}>
          {/* Header */}
          <div style={{
            padding: "13px 18px", fontWeight: 700, fontSize: 14,
            color: "#fff",
            background: `linear-gradient(135deg,${C.navy},#2a4a9f)`,
            display: "flex", justifyContent: "space-between",
            alignItems: "center",
          }}>
            <span>📊 Question × Cohort — Answer Cross-tab</span>
            <span style={{ fontSize: 12, fontWeight: 400, opacity: .7 }}>
              Yes% shown per cell · hover for counts
            </span>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{
              width: "100%", borderCollapse: "collapse", minWidth: 700,
            }}>
              {/* Column headers = cohorts (or "All") */}
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  <th style={{
                    padding: "10px 16px", textAlign: "left",
                    fontSize: 11, fontWeight: 700, color: C.text3,
                    textTransform: "uppercase", letterSpacing: "0.5px",
                    borderBottom: `1px solid ${C.border}`,
                    position: "sticky", left: 0, background: "#f8fafc",
                    minWidth: 280, zIndex: 2,
                    borderRight: `1px solid ${C.border}`,
                  }}>
                    Question
                  </th>
                  {selected === "all"
                    ? (
                      <th style={{
                        padding: "10px 14px", textAlign: "center",
                        fontSize: 11, fontWeight: 700, color: C.navy,
                        borderBottom: `1px solid ${C.border}`,
                        minWidth: 110,
                      }}>
                        All Cohorts
                      </th>
                    )
                    : filtered.map(c => (
                      <th key={`${c.cohortName}||${c.cohortDate}`}
                        style={{
                          padding: "8px 10px", textAlign: "center",
                          fontSize: 11, fontWeight: 700, color: C.text2,
                          borderBottom: `1px solid ${C.border}`,
                          borderLeft: `1px solid ${C.border}`,
                          minWidth: 120,
                        }}>
                        <div style={{ color: C.navy, fontWeight: 700 }}>
                          {c.cohortName}
                        </div>
                        {c.cohortDate && (
                          <div style={{
                            fontSize: 10, color: C.text3,
                            fontWeight: 400, marginTop: 2,
                          }}>
                            {dayjs(c.cohortDate).format("DD MMM YY")}
                          </div>
                        )}
                        {c.qaLabel && (
                          <div style={{
                            fontSize: 9, fontWeight: 700, marginTop: 2,
                            color: (c.scheduleKey||'pratham')==='campus' ? '#16a34a' : C.blue,
                            background: (c.scheduleKey||'pratham')==='campus' ? '#f0fdf4' : C.lblue,
                            padding: '1px 5px', borderRadius: 8, display: 'inline-block',
                          }}>
                            {c.qaLabel} Q&A
                          </div>
                        )}
                        <div style={{
                          fontSize: 10, color: C.green,
                          fontWeight: 600, marginTop: 2,
                        }}>
                          {c.totalCompleted}/{c.totalEnrolled} done
                        </div>
                      </th>
                    ))
                  }
                </tr>
              </thead>
              <tbody>
                {questions.map((q, idx) => (
                  <tr key={q.key} style={{
                    borderBottom: `1px solid ${C.border}`,
                    background: idx % 2 === 0 ? C.white : "#fafcff",
                  }}>
                    {/* Question label */}
                    <td style={{
                      padding: "10px 16px",
                      position: "sticky", left: 0,
                      background: idx % 2 === 0 ? C.white : "#fafcff",
                      borderRight: `1px solid ${C.border}`,
                      zIndex: 1,
                    }}>
                      <div style={{
                        display: "flex", alignItems: "flex-start", gap: 8,
                      }}>
                        <span style={{
                          width: 22, height: 22, borderRadius: 6,
                          background: C.navy, color: "#fff",
                          display: "flex", alignItems: "center",
                          justifyContent: "center", fontSize: 10,
                          fontWeight: 800, flexShrink: 0,
                        }}>
                          {idx + 1}
                        </span>
                        <span style={{
                          fontSize: 12, color: C.text1,
                          fontWeight: 600, lineHeight: 1.4,
                        }}>
                          {q.label}
                        </span>
                      </div>
                    </td>

                    {/* Per-cohort cells */}
                    {selected === "all"
                      ? (() => {
                          const agg = aggregate.find(a => a.key === q.key);
                          const qAType = q.answerType || 'yesno';
                          const noAggData = !agg || agg.responded === 0;
                          if (noAggData) return <td style={{ padding:"10px 14px", textAlign:"center", color:C.border }}>—</td>;

                          if (qAType === 'rating5') {
                            const rc2 = agg.avg>=4?C.green:agg.avg>=3?C.amber:C.red;
                            return (
                              <td style={{ padding:"8px 10px", textAlign:"center" }}>
                                <Tooltip title={<div style={{fontSize:11}}>Count:{agg.count} · Min:{agg.min} · Max:{agg.max} · Avg:{agg.avg}/5</div>}>
                                  <div>
                                    <div style={{ fontSize:16,fontWeight:900,color:rc2 }}>{agg.avg}<span style={{fontSize:11,color:C.text3}}>/5</span></div>
                                    <div style={{ fontSize:10,color:C.text3 }}>
                                      {agg.avg>=4?"😊":agg.avg>=3?"😐":"😟"}
                                    </div>
                                  </div>
                                </Tooltip>
                              </td>
                            );
                          }
                          if (qAType === 'choice') {
                            const cc2 = agg.choiceCounts||{};
                            return (
                              <td style={{ padding:"6px 8px", textAlign:"center" }}>
                                <Tooltip title={<div style={{fontSize:11}}>{Object.entries(cc2).map(([k,v])=><div key={k}>{k}:{v}</div>)}</div>}>
                                  <div>
                                    {cc2['Daily']>0&&<div style={{fontSize:10,fontWeight:700,color:C.blue}}>📅{cc2['Daily']}</div>}
                                    {cc2['Call by Call']>0&&<div style={{fontSize:10,fontWeight:700,color:C.purple}}>📞{cc2['Call by Call']}</div>}
                                  </div>
                                </Tooltip>
                              </td>
                            );
                          }

                          const aggPos = INVERTED_KEYS.has(q.key)
                            ? (agg.yes+agg.no>0?Math.round((agg.no/(agg.yes+agg.no))*100):0)
                            : agg.yesPct;
                          const col = signalColor(aggPos);
                          const bg  = signalBg(aggPos);
                          return (
                            <td style={{ padding:"8px 10px", textAlign:"center" }}>
                              <Tooltip title={<div style={{fontSize:12}}>
                                <div>✓ Yes:<strong>{agg.yes}</strong></div>
                                <div>✗ No:<strong>{agg.no}</strong></div>
                                <div>– NA:<strong>{agg.na}</strong></div>
                                <div>Total:<strong>{agg.responded}</strong></div>
                                {INVERTED_KEYS.has(q.key) && <div style={{color:C.green,fontWeight:700}}>No = positive for this Q</div>}
                              </div>}>
                                <div style={{ fontSize:15,fontWeight:800,color:col,marginBottom:4 }}>{aggPos}%</div>
                                <PctBar pct={aggPos} color={col} h={5}/>
                                <div style={{ display:"flex",justifyContent:"center",gap:5,marginTop:5 }}>
                                  {INVERTED_KEYS.has(q.key)
                                    ? <><Pill count={agg.no} type="yes"/><Pill count={agg.yes} type="no"/></>
                                    : <><Pill count={agg.yes} type="yes"/><Pill count={agg.no} type="no"/><Pill count={agg.na} type="na"/></>
                                  }
                                </div>
                              </Tooltip>
                            </td>
                          );
                        })()
                      : filtered.map(c => {
                          const qs = c.questionSummary.find(x => x.key === q.key);
                          if (!qs || qs.responded === 0) return (
                            <td key={`${c.cohortName}||${c.cohortDate}`}
                              style={{
                                padding: "10px", textAlign: "center",
                                borderLeft: `1px solid ${C.border}`,
                                color: "#e2e8f0",
                              }}>
                              —
                            </td>
                          );
                          const col = signalColor(qs.yesPct);
                          return (
                            <td key={`${c.cohortName}||${c.cohortDate}`}
                              style={{
                                padding: "8px 10px",
                                borderLeft: `1px solid ${C.border}`,
                                textAlign: "center",
                              }}>
                              <Tooltip title={
                                <div style={{ fontSize: 12 }}>
                                  <div>✓ Yes: <strong>{qs.yes}</strong></div>
                                  <div>✗ No:  <strong>{qs.no}</strong></div>
                                  <div>– NA:  <strong>{qs.na}</strong></div>
                                  <div>Responded: <strong>{qs.responded}</strong></div>
                                </div>
                              }>
                                <div style={{
                                  fontSize: 14, fontWeight: 800,
                                  color: col, marginBottom: 3,
                                }}>
                                  {qs.yesPct}%
                                </div>
                                <PctBar pct={qs.yesPct} color={col} h={4}/>
                                <div style={{
                                  display: "flex", justifyContent: "center",
                                  gap: 4, marginTop: 4,
                                }}>
                                  <Pill count={qs.yes} type="yes"/>
                                  <Pill count={qs.no}  type="no"/>
                                  <Pill count={qs.na}  type="na"/>
                                </div>
                              </Tooltip>
                            </td>
                          );
                        })
                    }
                  </tr>
                ))}
              </tbody>

              {/* Footer row — overall Yes% per cohort */}
              {selected !== "all" && (
                <tfoot>
                  <tr style={{ background: C.navy }}>
                    <td style={{
                      padding: "11px 16px", fontWeight: 800, color: "#fff",
                      fontSize: 13, position: "sticky", left: 0,
                      background: C.navy,
                    }}>
                      Avg Yes% across all questions
                    </td>
                    {filtered.map(c => {
                      const avgYes = c.questionSummary.length
                        ? Math.round(
                            c.questionSummary.reduce((s,q)=>s+q.yesPct,0)
                            / c.questionSummary.length
                          )
                        : 0;
                      const col = avgYes >= 80 ? "#4ade80"
                        : avgYes >= 50 ? "#fbbf24" : "#f87171";
                      return (
                        <td key={`${c.cohortName}||${c.cohortDate}`}
                          style={{
                            padding: "11px 10px", textAlign: "center",
                            borderLeft: "1px solid rgba(255,255,255,0.15)",
                          }}>
                          <span style={{
                            fontSize: 16, fontWeight: 800, color: col,
                          }}>
                            {avgYes}%
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {/* ══ COHORT × QUESTION CROSS-TAB TABLE ════════════════════ */}
      {filtered.length > 0 && view === "ctable" && (
        <div style={{
          background: C.white, overflow: "hidden",
        }}>
          {/* Table header */}
          <div style={{
            padding: "14px 20px",
            background: `linear-gradient(135deg,${C.navy},#2a4a9f)`,
            display: "flex", justifyContent: "space-between",
            alignItems: "center",
          }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>
                📊 Day-10 Completion per Cohort
              </div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", marginTop: 3 }}>
                Cohort rows · Questions as columns · Yes / No for choice questions · Avg for numeric
              </div>
            </div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>
              {filtered.length} cohorts · {questions.length} questions
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 800 }}>

              {/* ── Row 1: Question numbers spanning sub-columns ── */}
              <thead>
                <tr style={{ background: C.navy }}>
                  {/* Cohort column header */}
                  <th rowSpan={2} style={{
                    padding: "10px 16px", textAlign: "left",
                    fontSize: 12, fontWeight: 700, color: "#fff",
                    borderRight: `1px solid rgba(255,255,255,0.15)`,
                    position: "sticky", left: 0, background: C.navy,
                    minWidth: 180, zIndex: 3,
                    verticalAlign: "middle",
                  }}>
                    Cohort
                  </th>
                  {/* Completed column */}
                  <th rowSpan={2} style={{
                    padding: "10px 12px", textAlign: "center",
                    fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.7)",
                    borderRight: `1px solid rgba(255,255,255,0.15)`,
                    minWidth: 80, verticalAlign: "middle",
                  }}>
                    Done
                  </th>
                  {/* Question group headers */}
                  {questions.map((q, idx) => {
                    const isSingle = q.answerType === "integer" || q.answerType === "rating5" || q.answerType === "choice";
                    return (
                      <th key={q.key}
                        colSpan={isSingle ? 1 : 2}
                        style={{
                          padding: "8px 10px", textAlign: "center",
                          fontSize: 11, fontWeight: 800, color: "#fff",
                          borderLeft: `1px solid rgba(255,255,255,0.15)`,
                          borderBottom: `1px solid rgba(255,255,255,0.2)`,
                          minWidth: isSingle ? (q.answerType === "choice" ? 110 : 70) : 110,
                        }}>
                        <Tooltip title={`Q${idx+1}: ${q.label}`}>
                          <span style={{ cursor: "help" }}>Q{idx+1}</span>
                        </Tooltip>
                      </th>
                    );
                  })}
                </tr>

                {/* ── Row 2: Sub-column labels (Yes/No or Avg) ── */}
                <tr style={{ background: "#1e3a8a" }}>
                  {questions.map(q => {
                    const aType = q.answerType || 'yesno';
                    const thStyle = { padding:"5px 8px", textAlign:"center", fontSize:10, fontWeight:700,
                      borderLeft:`1px solid rgba(255,255,255,0.1)`, borderTop:`1px solid rgba(255,255,255,0.1)` };
                    if (aType === 'rating5') return (
                      <th key={q.key} style={{...thStyle, color:"#fde68a"}}>Avg/5</th>
                    );
                    if (aType === 'integer') return (
                      <th key={q.key} style={{...thStyle, color:"#93c5fd"}}>Avg</th>
                    );
                    if (aType === 'choice') return (
                      <th key={q.key} style={{...thStyle, color:"#c4b5fd"}}>Option</th>
                    );
                    return (
                      <React.Fragment key={q.key}>
                        <th style={{...thStyle, color:"#86efac"}}>Yes</th>
                        <th style={{...thStyle, color:"#fca5a5", borderLeft:"none"}}>No</th>
                      </React.Fragment>
                    );
                  })}
                </tr>
              </thead>

              {/* ── Body: one row per cohort ── */}
              <tbody>
                {filtered.map((c, ri) => {
                  const pct = c.completionPct;
                  const pcol = signalColor(pct);
                  return (
                    <tr key={`${c.cohortName}||${c.cohortDate}`}
                      style={{
                        borderBottom: `1px solid ${C.border}`,
                        background: ri % 2 === 0 ? C.white : "#fafcff",
                      }}>

                      {/* Cohort name + date + completion bar */}
                      <td style={{
                        padding: "10px 14px",
                        position: "sticky", left: 0, zIndex: 1,
                        background: ri % 2 === 0 ? C.white : "#fafcff",
                        borderRight: `1px solid ${C.border}`,
                        minWidth: 180,
                      }}>
                        <div style={{
                          fontWeight: 700, fontSize: 13, color: C.text1,
                        }}>
                          {c.cohortName}
                        </div>
                        {c.cohortDate && (
                          <div style={{
                            fontSize: 10, color: C.text3, marginTop: 1,
                          }}>
                            {dayjs(c.cohortDate).format("DD MMM YYYY")}
                          </div>
                        )}
                        <div style={{ marginTop: 5 }}>
                          <PctBar pct={pct} color={pcol} h={4}/>
                        </div>
                      </td>

                      {/* Done / Enrolled */}
                      <td style={{
                        padding: "10px 12px", textAlign: "center",
                        borderRight: `1px solid ${C.border}`,
                      }}>
                        <div style={{
                          fontWeight: 800, fontSize: 14,
                          color: pcol,
                        }}>
                          {c.totalCompleted}
                        </div>
                        <div style={{
                          fontSize: 10, color: C.text3, marginTop: 1,
                        }}>
                          /{c.totalEnrolled}
                        </div>
                      </td>

                      {/* Per-question cells */}
                      {questions.map(q => {
                        const qs = c.questionSummary.find(x => x.key === q.key);
                        const noData = !qs || qs.responded === 0;

                        const aType = q.answerType || 'yesno';

                        // ── Rating 1-5 ─────────────────────────────────
                        if (aType === 'rating5') {
                          const ratingColor = (r) => r>=4?C.green:r===3?C.amber:C.red;
                          return (
                            <td key={q.key} style={{ padding:"8px 10px", textAlign:"center", borderLeft:`1px solid ${C.border}` }}>
                              {noData || qs.avg === null
                                ? <span style={{ color:"#e2e8f0" }}>—</span>
                                : (
                                  <Tooltip title={<div style={{fontSize:11}}>
                                    <div>Count: {qs.count}</div>
                                    <div>Min: {qs.min}</div>
                                    <div>Max: {qs.max}</div>
                                    <div>Avg: {qs.avg}/5</div>
                                  </div>}>
                                    <div>
                                      <div style={{ fontWeight:800, fontSize:15,
                                        color:ratingColor(qs.avg), cursor:"help" }}>
                                        {qs.avg}
                                      </div>
                                      <div style={{ fontSize:9, color:C.text3 }}>/5</div>
                                    </div>
                                  </Tooltip>
                                )}
                            </td>
                          );
                        }

                        // ── Choice (Daily / Call by Call) ──────────────
                        if (aType === 'choice') {
                          const cc = qs?.choiceCounts || {};
                          const daily = cc['Daily'] || 0;
                          const callByCall = cc['Call by Call'] || 0;
                          const total = qs?.responded || 0;
                          return (
                            <td key={q.key} style={{ padding:"6px 8px", textAlign:"center", borderLeft:`1px solid ${C.border}` }}>
                              {total === 0
                                ? <span style={{ color:"#e2e8f0" }}>—</span>
                                : (
                                  <Tooltip title={<div style={{fontSize:11}}>
                                    <div>Daily: {daily}</div>
                                    <div>Call by Call: {callByCall}</div>
                                    <div>Total: {total}</div>
                                  </div>}>
                                    <div style={{ cursor:"help" }}>
                                      {daily > 0 && (
                                        <div style={{ fontSize:10, fontWeight:700, color:C.blue }}>
                                          D:{daily}
                                        </div>
                                      )}
                                      {callByCall > 0 && (
                                        <div style={{ fontSize:10, fontWeight:700, color:C.purple }}>
                                          C:{callByCall}
                                        </div>
                                      )}
                                    </div>
                                  </Tooltip>
                                )}
                            </td>
                          );
                        }

                        // ── Integer avg ────────────────────────────────
                        if (aType === 'integer') {
                          return (
                            <td key={q.key} style={{
                              padding: "10px 10px", textAlign: "center",
                              borderLeft: `1px solid ${C.border}`,
                            }}>
                              {noData || qs.avg === null
                                ? <span style={{ color: "#e2e8f0" }}>—</span>
                                : (
                                  <Tooltip title={
                                    <div style={{ fontSize: 11 }}>
                                      <div>Count: {qs.count}</div>
                                      <div>Min: {qs.min}</div>
                                      <div>Max: {qs.max}</div>
                                      <div>Avg: {qs.avg}</div>
                                    </div>
                                  }>
                                    <span style={{
                                      fontWeight: 800, fontSize: 14,
                                      color: C.blue, cursor: "help",
                                    }}>
                                      {qs.avg}
                                    </span>
                                  </Tooltip>
                                )
                              }
                            </td>
                          );
                        }

                        // Yes / No columns
                        const yesPct = noData ? 0 : qs.yesPct;
                        const ycol   = noData ? C.border : signalColor(yesPct);
                        return (
                          <React.Fragment key={q.key}>
                            {/* Yes */}
                            <td style={{
                              padding: "10px 8px", textAlign: "center",
                              borderLeft: `1px solid ${C.border}`,
                            }}>
                              {noData
                                ? <span style={{ color: "#e2e8f0" }}>—</span>
                                : (
                                  <Tooltip title={`${qs.yes} Yes out of ${qs.responded} (${qs.yesPct}%)`}>
                                    <div>
                                      <div style={{
                                        fontWeight: 800, fontSize: 14,
                                        color: C.green,
                                      }}>
                                        {qs.yes}
                                      </div>
                                      <div style={{
                                        fontSize: 10, color: C.text3,
                                        marginTop: 1,
                                      }}>
                                        {qs.yesPct}%
                                      </div>
                                    </div>
                                  </Tooltip>
                                )
                              }
                            </td>
                            {/* No */}
                            <td style={{
                              padding: "10px 8px", textAlign: "center",
                            }}>
                              {noData
                                ? <span style={{ color: "#e2e8f0" }}>—</span>
                                : (
                                  <Tooltip title={`${qs.no} No out of ${qs.responded}`}>
                                    <div style={{
                                      fontWeight: qs.no > 0 ? 800 : 400,
                                      fontSize: 14,
                                      color: qs.no > 0 ? C.red : C.text3,
                                    }}>
                                      {qs.no}
                                    </div>
                                  </Tooltip>
                                )
                              }
                            </td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>

              {/* ── Footer: totals / averages across all cohorts ── */}
              <tfoot>
                <tr style={{ background: C.navy }}>
                  <td style={{
                    padding: "11px 14px", fontWeight: 800,
                    color: "#fff", fontSize: 13,
                    position: "sticky", left: 0, background: C.navy,
                    borderRight: `1px solid rgba(255,255,255,0.15)`,
                  }}>
                    TOTAL / AVG
                  </td>
                  <td style={{
                    padding: "11px 12px", textAlign: "center",
                    fontWeight: 800, color: "#4ade80", fontSize: 14,
                    borderRight: `1px solid rgba(255,255,255,0.15)`,
                  }}>
                    {filtered.reduce((s,c)=>s+c.totalCompleted,0)}
                    <span style={{ color: "rgba(255,255,255,0.4)", fontWeight: 400, fontSize: 11 }}>
                      /{filtered.reduce((s,c)=>s+c.totalEnrolled,0)}
                    </span>
                  </td>
                  {questions.map(q => {
                    const fType = q.answerType || 'yesno';

                    if (fType === 'rating5') {
                      const vals = filtered
                        .map(c => c.questionSummary.find(x=>x.key===q.key)?.avg)
                        .filter(v => v !== null && v !== undefined);
                      const overallAvg = vals.length
                        ? Math.round(vals.reduce((s,v)=>s+v,0)/vals.length*10)/10 : null;
                      const rc = (r) => r>=4?"#4ade80":r===3?"#fbbf24":"#f87171";
                      return (
                        <td key={q.key} style={{ padding:"11px 10px", textAlign:"center", borderLeft:`1px solid rgba(255,255,255,0.15)` }}>
                          <span style={{ fontWeight:800, fontSize:14, color: overallAvg?rc(overallAvg):"rgba(255,255,255,0.3)" }}>
                            {overallAvg !== null ? `${overallAvg}/5` : "—"}
                          </span>
                        </td>
                      );
                    }

                    if (fType === 'choice') {
                      const allCounts = { 'Daily': 0, 'Call by Call': 0 };
                      filtered.forEach(c => {
                        const qs2 = c.questionSummary.find(x=>x.key===q.key);
                        Object.entries(qs2?.choiceCounts||{}).forEach(([k,v]) => { allCounts[k]=(allCounts[k]||0)+v; });
                      });
                      return (
                        <td key={q.key} style={{ padding:"8px 10px", textAlign:"center", borderLeft:`1px solid rgba(255,255,255,0.15)` }}>
                          {allCounts['Daily']>0&&<div style={{fontSize:10,fontWeight:700,color:"#93c5fd"}}>D:{allCounts['Daily']}</div>}
                          {allCounts['Call by Call']>0&&<div style={{fontSize:10,fontWeight:700,color:"#c4b5fd"}}>C:{allCounts['Call by Call']}</div>}
                        </td>
                      );
                    }

                    if (fType === 'integer') {
                      // Average of averages
                      const vals = filtered
                        .map(c => c.questionSummary.find(x=>x.key===q.key)?.avg)
                        .filter(v => v !== null && v !== undefined);
                      const overallAvg = vals.length
                        ? Math.round(vals.reduce((s,v)=>s+v,0)/vals.length*10)/10
                        : null;
                      return (
                        <td key={q.key} style={{
                          padding: "11px 10px", textAlign: "center",
                          borderLeft: `1px solid rgba(255,255,255,0.15)`,
                        }}>
                          <span style={{
                            fontWeight: 800, fontSize: 14, color: "#93c5fd",
                          }}>
                            {overallAvg !== null ? overallAvg : "—"}
                          </span>
                        </td>
                      );
                    }
                    // Yes / No totals
                    const totalYes = filtered.reduce((s,c) => {
                      const qs = c.questionSummary.find(x=>x.key===q.key);
                      return s + (qs?.yes||0);
                    },0);
                    const totalNo = filtered.reduce((s,c) => {
                      const qs = c.questionSummary.find(x=>x.key===q.key);
                      return s + (qs?.no||0);
                    },0);
                    const totalResp = filtered.reduce((s,c) => {
                      const qs = c.questionSummary.find(x=>x.key===q.key);
                      return s + (qs?.responded||0);
                    },0);
                    const totalYesPct = totalResp>0
                      ? Math.round((totalYes/totalResp)*100) : 0;
                    return (
                      <React.Fragment key={q.key}>
                        <td style={{
                          padding: "11px 8px", textAlign: "center",
                          borderLeft: `1px solid rgba(255,255,255,0.15)`,
                        }}>
                          <div style={{
                            fontWeight: 800, fontSize: 14, color: "#4ade80",
                          }}>
                            {totalYes}
                          </div>
                          <div style={{
                            fontSize: 10, color: "rgba(255,255,255,0.5)",
                          }}>
                            {totalYesPct}%
                          </div>
                        </td>
                        <td style={{
                          padding: "11px 8px", textAlign: "center",
                        }}>
                          <span style={{
                            fontWeight: 800, fontSize: 14,
                            color: totalNo > 0 ? "#f87171" : "rgba(255,255,255,0.3)",
                          }}>
                            {totalNo}
                          </span>
                        </td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Legend + Question Reference */}
          <div style={{
            padding: "14px 20px",
            background: C.surf,
            borderTop: `1px solid ${C.border}`,
          }}>
            {/* Answer type legend */}
            <div style={{
              display: "flex", gap: 20, flexWrap: "wrap",
              alignItems: "center", marginBottom: 14,
            }}>
              <span style={{ fontSize: 11, color: C.text3, fontWeight: 600 }}>
                📌 Legend:
              </span>
              <span style={{ fontSize: 11, color: C.green, fontWeight: 600 }}>
                ✓ Yes = positive response
              </span>
              <span style={{ fontSize: 11, color: C.red, fontWeight: 600 }}>
                ✗ No = needs follow-up
              </span>
              <span style={{ fontSize: 11, color: C.blue, fontWeight: 600 }}>
                # Avg = average of numeric / scale answers
              </span>
            </div>

            {/* Full question reference */}
            <div style={{
              fontSize: 12, fontWeight: 700, color: C.text2,
              marginBottom: 10, display: "flex", alignItems: "center", gap:8,
            }}>
              📋 Question Reference
            </div>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(420px, 1fr))",
              gap: "6px 24px",
            }}>
              {questions.map((q, idx) => (
                <div key={q.key} style={{
                  display: "flex", alignItems: "flex-start",
                  gap: 8, fontSize: 11, color: C.text2,
                }}>
                  <span style={{
                    minWidth: 24, height: 20, borderRadius: 5,
                    background: q.answerType === "integer" ? C.lblue : C.lgreen,
                    color: q.answerType === "integer" ? C.blue : C.green,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: 800, fontSize: 10, flexShrink: 0,
                  }}>
                    Q{idx + 1}
                  </span>
                  <span style={{ lineHeight: 1.5, color: C.text1 }}>
                    {q.label}
                    {q.answerType === "integer" && (
                      <span style={{
                        marginLeft: 6, fontSize: 10, color: C.blue,
                        fontWeight: 700, background: C.lblue,
                        padding: "0px 5px", borderRadius: 10,
                      }}>
                        numeric
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      </div>{/* end tab panel wrapper */}

    </div>
  );
}